import { observer } from 'mobx-react-lite';
import styled from '@emotion/styled';
import { useState } from 'react';
import { ContentSearchStore } from '../domain/stores/createContentSearchStore';
import { Css, Flex } from '../../../ui';
import { FlightSearchList } from './FlightSearch/FlightSearchList';
import { SearchEmptyState } from './SearchEmptyState';
import { InformationSearchList } from './InformationSearch/InformationSearchList';
import {
	Message,
	NoResults,
} from '../../../components/common/no-results/NoResults';
import { Color } from '../../../styles/Color';
import { PoiStore } from '../../shop-dine-relax/search/domain/hooks/usePoiStore';
import { LocationSearchList } from './LocationSearch/LocationSearchList';
import SearchFilters from './SearchFilters';
import { getNoResultsData } from '../use-cases/getNoResultsData';
import { QuickLink } from '../../../components/common/quick-links/QuickLink';
import { ErrorState } from '../../../components/common/error-state/ErrorState';
import { FlightListStore } from '../../flights/overview/domain/stores/FlightListStore';
import { QUERY_MIN_LENGTH } from '../../../components/common/global-search/domain/constants';

interface IProps {
	flightStore: FlightListStore;
	locationStore: PoiStore;
	informationStore: ContentSearchStore;
	isDebouncing: boolean;
	query: string;
	scrollRef: React.RefObject<HTMLDivElement>;
	updateQuery: (query: string) => void;
}

export const SearchOverlayContent = observer(
	({
		flightStore,
		locationStore,
		informationStore,
		isDebouncing,
		query,
		scrollRef,
		updateQuery,
	}: IProps) => {
		const [filterFlights, setFilterFlights] = useState(false);
		const [filterLocations, setFilterLocations] = useState(false);
		const [filterInformation, setFilterInformation] = useState(false);

		const flightsHasError = !!flightStore.error;
		const locationsHasError = !!locationStore.data.error;
		const informationHasError = !!informationStore.error;

		const flightsHasNoData = flightStore.flights.length === 0;
		const locationsHasNoData = locationStore.data.filteredData.length === 0;
		const informationHasNoData = informationStore.content.length === 0;

		const flightsIsLoading = flightStore.isLoading;
		const locationsIsLoading = locationStore.data.loading;
		const informationIsLoading = locationStore.data.loading;

		const isLoading =
			flightsIsLoading ||
			locationsIsLoading ||
			informationIsLoading ||
			isDebouncing;

		const noFiltersSelected =
			!filterFlights && !filterLocations && !filterInformation;
		const showFlights = noFiltersSelected || filterFlights;
		const showLocations = noFiltersSelected || filterLocations;
		const showInformation = noFiltersSelected || filterInformation;

		const showNoData =
			(!showFlights || (flightsHasNoData && !flightsHasError)) &&
			(!showLocations || (locationsHasNoData && !locationsHasError)) &&
			(!showInformation || (informationHasNoData && !informationHasError)) &&
			!isLoading;

		const showSearchEmpty = query.length < QUERY_MIN_LENGTH;
		const showFilters = !showSearchEmpty;

		function renderNoData() {
			const noResultsData = getNoResultsData(
				filterFlights,
				filterLocations,
				filterInformation,
			);

			return (
				<NoResultsWrapper>
					<StyledNoResults
						icon={noResultsData.icon}
						title={noResultsData.title}
						message={noResultsData.message}
					/>
					{noResultsData.link && (
						<QuickLink type="internal" url={noResultsData.link.url}>
							{noResultsData.link.urlText}
						</QuickLink>
					)}
				</NoResultsWrapper>
			);
		}

		function renderContent() {
			return (
				<ContentWrapper direction="column" gap="24">
					{showFlights && (
						<FlightSearchList
							store={flightStore}
							query={query}
							isDebouncing={isDebouncing}
						/>
					)}
					{showLocations && (
						<LocationSearchList
							store={locationStore}
							query={query}
							isDebouncing={isDebouncing}
						/>
					)}
					{showInformation && (
						<InformationSearchList
							store={informationStore}
							isDebouncing={isDebouncing}
						/>
					)}
				</ContentWrapper>
			);
		}

		function renderErrors() {
			return (
				<>
					{flightsHasError && showFlights && (
						<StyledErrorState
							title="Unable to retrieve flight data"
							message="Please wait a few minutes and try again."
						/>
					)}
					{locationsHasError && showLocations && (
						<StyledErrorState
							title="Unable to retrieve location data"
							message="Please wait a few minutes and try again."
						/>
					)}
					{informationHasError && showInformation && (
						<StyledErrorState
							title="Unable to retrieve information data"
							message="Please wait a few minutes and try again."
						/>
					)}
				</>
			);
		}

		return (
			<Container>
				{showFilters && (
					<SearchFilters
						filterFlights={filterFlights}
						setFilterFlights={setFilterFlights}
						filterLocations={filterLocations}
						setFilterLocations={setFilterLocations}
						filterInformation={filterInformation}
						setFilterInformation={setFilterInformation}
						scrollRef={scrollRef}
					/>
				)}
				{showSearchEmpty ? (
					<SearchEmptyState updateQuery={updateQuery} />
				) : (
					<>
						{showNoData ? renderNoData() : renderContent()}
						{!isLoading && renderErrors()}
					</>
				)}
			</Container>
		);
	},
);

const Container = styled(Flex)`
	min-height: 100%;
`;

const NoResultsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Css.pxToRem(16)};
	padding: ${Css.pxToRem(80)} 0;
	align-items: center;
`;

const StyledNoResults = styled(NoResults)`
	svg {
		width: ${Css.pxToRem(48)};
		height: ${Css.pxToRem(48)};
		color: ${Color.PrimaryBlue1};
	}
	padding: 0;
	margin: 0 ${Css.pxToRem(24)};

	max-width: none;
	${Message} {
		max-width: ${Css.pxToRem(300)};
	}
`;

const StyledErrorState = styled(ErrorState)`
	max-width: none;
	margin: 0 ${Css.pxToRem(24)};
`;

const ContentWrapper = styled(Flex)`
	padding: 0 ${Css.pxToRem(16)} ${Css.pxToRem(16)} ${Css.pxToRem(16)};
`;
